import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Star, Calendar, Users, Compass, ArrowRight } from "lucide-react";
import Hero from "../components/Hero";
import TestimonialsMarquee from "../components/TestimonialsMarquee";
import VideoSection from "../components/VideoSection";
import FeaturedFleet from "../components/FeaturedFleet";
import Gallery from "../components/Gallery";
import { ASSETS } from "../lib/constants";

export default function Home({ onBookNow }) {
  const { t } = useTranslation();

  return (
    <>
      <Hero onBookNow={onBookNow} />
      <VideoSection />
      <TestimonialsMarquee />

      {/* Stats strip */}
      <section className="border-b border-white/10 bg-brand-black">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/10 md:grid-cols-4">
          {[
            { k: "135+", v: t("proof.reviews") },
            { k: "5.0 ★", v: t("proof.rating") },
            { k: "8+", v: t("proof.years") },
            { k: "1.5k", v: t("proof.riders") },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-brand-black px-6 py-8 text-center"
              data-testid={`stat-${i}`}
            >
              <div className="font-display text-4xl text-brand-orange md:text-5xl">{s.k}</div>
              <div className="mt-1 text-xs font-bold uppercase tracking-widest text-white/70">
                {s.v}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <FeaturedFleet onBookNow={onBookNow} />

      {/* Why us — Tetris bento */}
      <section className="bg-brand-black py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-4xl uppercase leading-none sm:text-5xl">
              {t("about.title")}
            </h2>
            <Link
              to="/about"
              className="text-xs font-bold uppercase tracking-widest text-brand-orange hover:underline"
            >
              → {t("about.title")}
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-6 md:grid-rows-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="md:col-span-3 md:row-span-2 relative overflow-hidden border-2 border-brand-orange bg-brand-black text-white"
            >
              <img src={ASSETS.lifestyle1} alt="" className="h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <h3 className="font-display text-3xl uppercase">San Juan del Sur</h3>
                <p className="mt-1 text-sm">{t("about.lead")}</p>
              </div>
            </motion.div>

            {[
              { icon: <Calendar />, title: t("about.feat1Title"), body: t("about.feat1Body") },
              { icon: <Users />, title: t("about.feat2Title"), body: t("about.feat2Body") },
              { icon: <Compass />, title: t("about.feat3Title"), body: t("about.feat3Body") },
              { icon: <Star />, title: t("proof.rating"), body: "Trusted by riders worldwide." },
            ].map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`border-2 border-brand-orange p-6 ${
                  i === 0 ? "md:col-span-3 bg-brand-orange text-white" : "md:col-span-1 bg-brand-black hard-shadow"
                }`}
              >
                <div className={`mb-2 inline-flex h-9 w-9 items-center justify-center ${i === 0 ? "bg-brand-black text-brand-orange" : "bg-brand-orange text-white"}`}>
                  {c.icon}
                </div>
                <h3 className="font-display text-2xl uppercase">{c.title}</h3>
                <p className="mt-1 text-sm leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Gallery />

      {/* Big CTA strip */}
      <section className="bg-brand-orange">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 py-14 sm:px-6 md:flex-row lg:px-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">{t("hero.eyebrow")}</p>
            <h3 className="font-display text-4xl uppercase text-white sm:text-5xl">
              {t("hero.bookNow")} →
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="cta-strip-book"
              onClick={onBookNow}
              className="inline-flex h-14 items-center gap-2 bg-brand-black px-6 font-display text-xl uppercase tracking-wider text-white hover:bg-brand-black/80 hover:text-brand-orange"
            >
              {t("hero.bookNow")} <ArrowRight className="h-5 w-5" />
            </button>
            <Link
              to="/rentals"
              className="inline-flex h-14 items-center gap-2 border-2 border-white px-6 font-display text-xl uppercase tracking-wider text-white hover:bg-brand-black hover:text-brand-orange"
            >
              {t("hero.viewMotos")} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
